<template>
    <v-dialog
        v-model="showModel"
        fullscreen
        hide-overlay
        persistent
        transition="dialog-bottom-transition"
        style="overflow-y: auto"
    >
        <v-card flat class="fill-height of-y" style="overflow-y: auto; overflow-x: hidden">
            <v-layout column fill-height style="overflow-y: auto; overflow-x: hidden">
                <!--Card Title-->
                <v-flex shrink>
                    <v-card-title class="py-2 pr-2 title-color">
                        <v-icon left color="white">security</v-icon>
                        <span class="title font-weight-light white--text">Impersonation Log</span>
                        <v-spacer></v-spacer>

                        <v-menu :close-on-content-click="false" offset-y left>
                            <template #activator="{on}">
                                <v-btn flat small color="white" class="pa-0 ma-0 mx-1" v-on="on">
                                    <v-icon left small>date_range</v-icon>
                                    {{ dateRangeLabel }}
                                </v-btn>
                            </template>
                            <v-card>
                                <v-card-text class="pa-2">
                                    <v-text-field
                                        label="From"
                                        type="date"
                                        v-model="fromDate"
                                        hide-details
                                        clearable
                                        class="pb-2"
                                    ></v-text-field>
                                    <v-text-field
                                        label="To"
                                        type="date"
                                        v-model="toDate"
                                        hide-details
                                        clearable
                                    ></v-text-field>
                                </v-card-text>
                                <v-card-actions>
                                    <v-btn flat small @click="clearDates()">Clear</v-btn>
                                    <v-spacer></v-spacer>
                                    <v-btn color="secondary" small @click="loadLog()">Apply</v-btn>
                                </v-card-actions>
                            </v-card>
                        </v-menu>

                        <v-text-field
                            class="mx-1"
                            label="Tenant ID"
                            type="number"
                            min="1"
                            v-model.number="entityId"
                            dense
                            hide-details
                            flat
                            clearable
                            dark
                            solo-inverted
                            style="max-width: 110px"
                            @change="loadLog()"
                            @keyup.enter="loadLog()"
                        ></v-text-field>

                        <v-tooltip bottom>
                            <template #activator="{on}">
                                <v-btn
                                    flat
                                    icon
                                    :color="missingReasonOnly ? 'amber' : 'white'"
                                    class="pa-0 ma-0 mx-1"
                                    v-on="on"
                                    @click="missingReasonOnly = !missingReasonOnly; loadLog()"
                                >
                                    <v-icon>{{ missingReasonOnly ? 'report_problem' : 'filter_list' }}</v-icon>
                                </v-btn>
                            </template>
                            {{ missingReasonOnly ? 'Showing only sessions with NO reason given' : 'Show only sessions with no reason given' }}
                        </v-tooltip>

                        <v-text-field
                            class="mx-1"
                            label="Search"
                            v-model="textFilter"
                            dense
                            hide-details
                            flat
                            clearable
                            dark
                            solo-inverted
                        ></v-text-field>

                        <v-tooltip bottom>
                            <template #activator="{on}">
                                <v-btn flat icon color="white" class="pa-0 ma-0" v-on="on" @click="loadLog">
                                    <div style="line-height: 1;" class="text-xs-center">
                                        {{ filteredRows.length }}
                                    </div>
                                </v-btn>
                            </template>
                            {{ totalLabel }}
                        </v-tooltip>

                        <v-tooltip bottom>
                            <template #activator="{on}">
                                <v-btn
                                    flat icon color="white" class="pa-0 ma-0"
                                    v-on="on"
                                    :loading="exporting"
                                    :disabled="exporting"
                                    @click="exportData()"
                                >
                                    <v-icon>file_download</v-icon>
                                </v-btn>
                            </template>
                            Export to Excel (all rows matching the current filters)
                        </v-tooltip>

                        <v-btn flat icon color="white" class="pa-0 ma-0" @click="loadLog">
                            <v-icon>refresh</v-icon>
                        </v-btn>
                        <v-btn flat icon color="white" class="pa-0 ma-0" @click="showModel = false">
                            <v-icon>close</v-icon>
                        </v-btn>
                    </v-card-title>
                </v-flex>

                <!--Loading-->
                <v-flex v-if="loading && rows.length === 0" class="pa-2 text-xs-center">
                    <v-progress-circular :size="40" :width="5" color="primary" indeterminate/>
                </v-flex>

                <!--Empty-->
                <v-flex v-else-if="!filteredRows.length" class="pa-4 text-xs-center greyType--text">
                    <v-icon large color="grey lighten-1">security</v-icon>
                    <div class="body-1 pt-2">No impersonation sessions recorded for this filter.</div>
                </v-flex>

                <!--Loaded-->
                <template v-else>
                    <v-flex xs12 class="scrollable fill-height">
                        <v-data-table
                            :headers="headers"
                            :items="filteredRows"
                            item-key="id"
                            :pagination.sync="pageData"
                            class="fill-height scroll-table-with-foot"
                        >
                            <template v-slot:items="props">
                                <td style="white-space: nowrap">
                                    {{ props.item.created ? format(new Date(props.item.created), 'YYYY-MM-DD HH:mm:ss') : '-' }}
                                </td>
                                <td>
                                    <span @click="copyStuff(props.item.admin_username)" class="underline-hover">{{ props.item.admin_username }}</span>
                                </td>
                                <td>
                                    <span @click="copyStuff(props.item.target_username)" class="underline-hover">{{ props.item.target_username }}</span>
                                </td>
                                <td>
                                    <span @click="copyStuff(props.item.entity_name)" class="underline-hover">
                                        {{ props.item.entity_name || '-' }}
                                        <span class="caption greyType--text" v-if="props.item.fk_entity">({{ props.item.fk_entity }})</span>
                                    </span>
                                </td>
                                <td style="min-width: 260px">
                                    <span v-if="props.item.reason">{{ props.item.reason }}</span>
                                    <span v-else class="red--text font-weight-medium">
                                        <v-icon small color="red">report_problem</v-icon>
                                        No reason given
                                    </span>
                                </td>
                                <td style="white-space: nowrap">
                                    <span @click="copyStuff(props.item.ip_address)" class="underline-hover">{{ props.item.ip_address || '-' }}</span>
                                </td>
                            </template>
                        </v-data-table>
                    </v-flex>
                </template>
            </v-layout>
        </v-card>
    </v-dialog>
</template>

<script>
import {codeBaseMixin} from "../../codeBaseMixin";
import format from "date-fns/format";
import {exportStats} from "@/codeFunctions";

export default {
    mixins: [codeBaseMixin],
    props: {
        show: Boolean
    },
    data() {
        return {
            loading: false,
            rows: [],
            total: 0,
            headers: [
                {text: 'When', value: 'created'},
                {text: 'Administrator', value: 'admin_username'},
                {text: 'Signed In As', value: 'target_username'},
                {text: 'Entity', value: 'entity_name'},
                {text: 'Reason', value: 'reason'},
                {text: 'IP', value: 'ip_address'},
            ],
            format,
            pageData: {
                descending: true,
                page: 1,
                rowsPerPage: 25,
                sortBy: 'created',
                totalItems: null
            },
            textFilter: null,
            fromDate: null,
            toDate: null,
            entityId: null,
            missingReasonOnly: false,
            exporting: false,
        }
    },
    watch: {
        show: {
            immediate: true,
            handler(shown) {
                if (shown) this.loadLog();
            }
        }
    },
    methods: {
        copyStuff(stuff) {
            if (stuff === null || stuff === undefined) return;
            try {
                navigator.clipboard.writeText(String(stuff));
                this.$snack.info('Copied to clipboard');
            } catch (e) {
                console.log(e);
            }
        },

        clearDates() {
            this.fromDate = null;
            this.toDate = null;
            this.loadLog();
        },

        clearFilters() {
            this.fromDate = null;
            this.toDate = null;
            this.entityId = null;
            this.missingReasonOnly = false;
            this.textFilter = null;
            this.loadLog();
        },

        // Server-side filters, shared by the grid and the export so both see the same set.
        filterParams() {
            const params = [];
            // Widen the bare dates to cover the whole day on each end.
            if (this.fromDate) params.push(`from=${encodeURIComponent(this.fromDate + ' 00:00:00')}`);
            if (this.toDate) params.push(`to=${encodeURIComponent(this.toDate + ' 23:59:59')}`);
            if (this.entityId) params.push(`entityId=${encodeURIComponent(this.entityId)}`);
            if (this.missingReasonOnly) params.push('missingReasonOnly=true');
            return params;
        },

        // Free-text search runs client side over whatever is loaded.
        applyTextFilter(rows) {
            if (!this.textFilter) return rows;
            const needle = (this.textFilter || '').toLowerCase();
            return rows.filter(r => (JSON.stringify(r) || '').toLowerCase().includes(needle));
        },

        async loadLog() {
            try {
                this.loading = true;
                const params = this.filterParams().concat('limit=1000');
                const data = await this.$newReq('get', `impersonationLog?${params.join('&')}`);
                this.rows = (data && Array.isArray(data.rows)) ? data.rows : [];
                this.total = (data && data.total) || 0;
            } catch (e) {
                console.log(e);
                this.$snack.networkError && this.$snack.networkError();
            } finally {
                this.loading = false;
            }
        },

        async exportData() {
            try {
                this.exporting = true;

                // The endpoint caps a page at 1000, so walk the offsets - an export that silently
                // stopped at the first page would be worse than useless for an audit.
                const base = this.filterParams();
                const all = [];
                for (let offset = 0; ; offset += 1000) {
                    const params = base.concat([`limit=1000`, `offset=${offset}`]);
                    const data = await this.$newReq('get', `impersonationLog?${params.join('&')}`);
                    const batch = (data && Array.isArray(data.rows)) ? data.rows : [];
                    all.push(...batch);
                    if (batch.length < 1000) break;
                }

                const rows = this.applyTextFilter(all);

                if (!rows.length)
                    return this.$snack.info('Nothing to export for the current filters');

                exportStats(
                    `Impersonation Log${this.entityId ? ' Tenant ' + this.entityId : ''} ${format(new Date(), 'YYYY-MM-DD')}`,
                    'Impersonation Log',
                    [
                        {text: 'When', value: 'created'},
                        {text: 'Administrator', value: 'admin_username'},
                        {text: 'Admin User ID', value: 'fk_admin_user'},
                        {text: 'Signed In As', value: 'target_username'},
                        {text: 'Target User ID', value: 'fk_target_user'},
                        {text: 'Tenant ID', value: 'fk_entity'},
                        {text: 'Entity', value: 'entity_name'},
                        {text: 'Reason', value: 'reason'},
                        {text: 'Reason Given At', value: 'reason_given_at'},
                        {text: 'IP Address', value: 'ip_address'},
                        {text: 'User Agent', value: 'user_agent'},
                    ],
                    rows.map(r => ({
                        ...r,
                        created: r.created ? format(new Date(r.created), 'YYYY-MM-DD HH:mm:ss') : '',
                        reason_given_at: r.reason_given_at ? format(new Date(r.reason_given_at), 'YYYY-MM-DD HH:mm:ss') : '',
                        reason: r.reason || 'NO REASON GIVEN',
                        entity_name: r.entity_name || '',
                        ip_address: r.ip_address || '',
                        user_agent: r.user_agent || '',
                    }))
                );
            } catch (e) {
                console.log(e);
                this.$snack.networkError && this.$snack.networkError();
            } finally {
                this.exporting = false;
            }
        },
    },
    computed: {
        filteredRows() {
            return this.applyTextFilter([...this.rows]);
        },

        dateRangeLabel() {
            if (!this.fromDate && !this.toDate) return 'All dates';
            return `${this.fromDate || 'start'} to ${this.toDate || 'now'}`;
        },

        totalLabel() {
            const missing = this.rows.filter(r => !r.reason).length;
            return `${this.total} recorded${missing ? ` - ${missing} shown with no reason` : ''} - click to refresh`;
        },

        showModel: {
            get() { return this.show; },
            set(val) { this.$emit('update:show', val); }
        }
    }
}
</script>

<style scoped>
.underline-hover:hover {
    cursor: pointer;
    text-decoration: underline;
    font-weight: bold;
    color: #000;
}
</style>
